/**
 * Per-row state for the bulk Focus Ask card. There is exactly one platform
 * action behind the card (the `create-asks` skill emits one MessageAction with
 * 1+ asks in `data.asks`), so rows are virtual: each one represents an entry
 * in that array. We assign each entry a stable client-side row id at first
 * render so removals don't shift indices and so React can key reliably.
 *
 * State tracked here:
 *   - `formByRowId`: the editable `FocusAskFormData` for each row, seeded once
 *     from the corresponding ask via `normalizeFocusAskPayload`.
 *   - `removedIds`: rows the user dismissed from the batch (kept client-side
 *     only — we never invoke `onCancel` on the platform).
 *
 * Platform status (`idle` / `started` / `finished` / `errored`) lives on the
 * single underlying MessageAction and is applied uniformly to every row by
 * the container; it is not tracked per-row here.
 */
import { useCallback, useMemo, useState } from 'react';

import { normalizeFocusAskPayload } from '../normalize-focus-ask-payload';
import type { FocusAskFormData } from '../types';

export type BulkFocusAskRow = {
	/** Stable client-side id, assigned at first render and persisted thereafter. */
	rowId: string;
	/** The original ask payload as received from the BE; used as the merge base. */
	originalAsk: unknown;
};

export type BulkFocusAskState = {
	rows: ReadonlyArray<BulkFocusAskRow>;
	getFormData: (rowId: string) => FocusAskFormData;
	updateField: <K extends keyof FocusAskFormData>(
		rowId: string,
		field: K,
		value: FocusAskFormData[K],
	) => void;
	updateFormData: (rowId: string, formData: FocusAskFormData) => void;
	removedIds: ReadonlySet<string>;
	removeRow: (rowId: string) => void;
	dismissAllRows: () => void;
};

const buildRowId = (invocationId: string, index: number): string =>
	`${invocationId || 'focus-ask'}#${index}`;

export const useBulkFocusAskState = (
	invocationId: string,
	asks: ReadonlyArray<unknown>,
): BulkFocusAskState => {
	// Synthesise stable rows from the initial asks array. We intentionally only
	// recompute when the underlying asks reference changes (i.e. a fresh message
	// arrives) — local edits/removals are layered on top via the state below.
	const rows = useMemo<BulkFocusAskRow[]>(
		() =>
			asks.map((ask, index) => ({
				rowId: buildRowId(invocationId, index),
				originalAsk: ask,
			})),
		[asks, invocationId],
	);

	const initialForms = useMemo(() => {
		const map: Record<string, FocusAskFormData> = {};
		for (const row of rows) {
			map[row.rowId] = normalizeFocusAskPayload(row.originalAsk);
		}
		return map;
	}, [rows]);

	const [formByRowId, setFormByRowId] = useState<Record<string, FocusAskFormData>>(initialForms);
	const [removedIds, setRemovedIds] = useState<ReadonlySet<string>>(() => new Set());

	const getFormData = useCallback(
		(rowId: string): FocusAskFormData => formByRowId[rowId] ?? initialForms[rowId],
		[formByRowId, initialForms],
	);

	const updateField = useCallback(
		<K extends keyof FocusAskFormData>(rowId: string, field: K, value: FocusAskFormData[K]) => {
			setFormByRowId((prev) => {
				const current = prev[rowId] ?? initialForms[rowId];
				if (!current) {
					return prev;
				}
				return {
					...prev,
					[rowId]: { ...current, [field]: value },
				};
			});
		},
		[initialForms],
	);

	const updateFormData = useCallback(
		(rowId: string, formData: FocusAskFormData) => {
			setFormByRowId((prev) => {
				if (!prev[rowId] && !initialForms[rowId]) {
					return prev;
				}
				return {
					...prev,
					[rowId]: formData,
				};
			});
		},
		[initialForms],
	);

	const removeRow = useCallback((rowId: string) => {
		setRemovedIds((prev) => {
			if (prev.has(rowId)) {
				return prev;
			}
			const next = new Set(prev);
			next.add(rowId);
			return next;
		});
	}, []);

	const dismissAllRows = useCallback(() => {
		setRemovedIds(new Set(rows.map((row) => row.rowId)));
	}, [rows]);

	return {
		rows,
		getFormData,
		updateField,
		updateFormData,
		removedIds,
		removeRow,
		dismissAllRows,
	};
};
